// frontend/src/components/Auth/NeedsProvisioning.tsx
import { useState, type ReactNode } from 'react';
import { MailQuestion, AlertCircle } from 'lucide-react';
import { Button, Card } from '../ui';
import { PLUS_ADDRESS_MESSAGE } from '../../utils/emailPolicy';

export interface NeedsProvisioningProps {
  /** The signed-in email (from the verified session). */
  email: string;
  /** Why sync refused an account (open-access plan §3.9). */
  reason: string;
  /** The email domains whose school currently has open access. */
  eligibleDomains: string[];
  /** Sign the user out (they hold a valid session but have no account). */
  onSignOut: () => void;
  /** Sign out and reopen Google's account picker. */
  onSwitchAccount: () => Promise<void>;
  /** Re-run sync (after a transient email-verification failure). */
  onRetry: () => Promise<void>;
}

/** The next 00:00 UTC (when the daily cap resets) in Eastern and Pacific time,
 *  e.g. "7 PM Eastern / 4 PM Pacific". Computed, not hardcoded: it is 8/5 PM
 *  in daylight saving time and 7/4 PM outside it. */
export function capResetTime(now: Date = new Date()): string {
  const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
  const at = (timeZone: string) =>
    new Intl.DateTimeFormat('en-US', { hour: 'numeric', timeZone }).format(next);
  return `${at('America/New_York')} Eastern / ${at('America/Los_Angeles')} Pacific`;
}

/** "@a.edu", "@a.edu or @b.edu", "@a.edu, @b.edu or @c.edu". */
function domainList(domains: string[]): string {
  const at = domains.map((d) => `@${d}`);
  if (at.length <= 1) return at.join('');
  return `${at.slice(0, -1).join(', ')} or ${at[at.length - 1]}`;
}

/** The eligible domain under the user's own domain, if any: a plain @umd.edu
 *  address gets "use @terpmail.umd.edu" rather than a generic list. */
function eligibleSubdomain(email: string, domains: string[]): string | undefined {
  const own = email.slice(email.lastIndexOf('@') + 1).toLowerCase();
  return own ? domains.find((d) => d.endsWith(`.${own}`)) : undefined;
}

const INVITED_LINE = 'If you were invited, sign in with the address the invitation was sent to.';

const SignedInAs =({ email }: { email: string }) => (
  <>
    You&rsquo;re signed in as <span className="font-medium text-ink">{email}</span>.{' '}
  </>
);

interface Copy {
  title: string;
  body: ReactNode;
  primary?: 'switch' | 'retry' | 'signout';
}

function copyFor(reason: string, email: string, domains: string[]): Copy {
  switch (reason) {
    case 'plus_address':
      return {
        title: 'Use your plain email address',
        body: <><SignedInAs email={email} />{PLUS_ADDRESS_MESSAGE}</>,
        primary: 'signout',
      };
    case 'not_eligible': {
      const sub = eligibleSubdomain(email, domains);
      return {
        title: 'Sign in with your school email',
        // An invitation sent to another address also lands here (sync matches
        // invitations on the signed-in email only), so both branches say how
        // to use it: staff at a school domain have no student address.
        body: sub ? (
          <>
            <SignedInAs email={email} />Students: use your @{sub} address instead.
            {' '}{INVITED_LINE}
          </>
        ) : (
          <>
            <SignedInAs email={email} />Students: sign in with your {domainList(domains)} address.
            {' '}{INVITED_LINE}
          </>
        ),
        primary: 'switch',
      };
    }
    case 'unconfirmed':
      return {
        title: 'Confirm your email',
        body: 'Please confirm your email address, then sign in again.',
        primary: 'signout',
      };
    case 'reauth':
      return {
        title: 'Sign in again',
        body: 'Please sign out and sign in again.',
        primary: 'signout',
      };
    case 'verification_unavailable':
      return {
        title: 'We couldn’t verify your email',
        body: 'We couldn’t verify your email just now. Please try again in a minute.',
        primary: 'retry',
      };
    case 'capacity':
      return {
        title: 'We’re letting students in gradually',
        body: `Today’s spots are taken. New spots open at ${capResetTime()}.`,
        primary: 'signout',
      };
    default: // no_grant
      return {
        title: 'This account doesn’t have access yet',
        body: (
          <>
            <SignedInAs email={email} />
            {INVITED_LINE}
            {domains.length > 0 && <> Students with a {domainList(domains)} address can sign in directly.</>}
          </>
        ),
        primary: 'switch',
      };
  }
}

/**
 * Shown when the session is authenticated but sync refused an account
 * (`needs_provisioning`). The copy depends on the reason (open-access plan
 * §3.9). Invite codes are retired (§3.6 R0), so there is no code form: new
 * students get in by their school domain or an admin invitation.
 */
const NeedsProvisioning = ({
  email, reason, eligibleDomains, onSignOut, onSwitchAccount, onRetry,
}: NeedsProvisioningProps) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // A retry that sync refuses again leaves this screen as it was; without a
  // note, the spinner stops and nothing says the retry happened.
  const [retried, setRetried] = useState(false);
  const copy = copyFor(reason, email, eligibleDomains);

  const run = async (action: () => Promise<void>, onDone?: () => void) => {
    setBusy(true);
    setError('');
    try {
      await action();
      onDone?.();
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const primary =
    copy.primary === 'switch' ? (
      <Button type="button" className="w-full" isLoading={busy} onClick={() => run(onSwitchAccount)}>
        Switch account
      </Button>
    ) : copy.primary === 'retry' ? (
      <Button
        type="button"
        className="w-full"
        isLoading={busy}
        onClick={() => run(onRetry, () => setRetried(true))}
      >
        Try again
      </Button>
    ) : (
      <Button type="button" className="w-full" onClick={onSignOut}>
        Sign out
      </Button>
    );

  return (
    <div className="min-h-screen bg-surface-ground flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md p-6">
        <div className="flex flex-col items-center text-center">
          <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-control bg-primary/10 text-primary">
            <MailQuestion className="h-6 w-6" />
          </span>
          <h1 className="text-lg font-semibold text-ink">{copy.title}</h1>
          <p className="mt-2 text-sm text-ink-2 leading-relaxed">{copy.body}</p>

          {copy.primary === 'retry' && retried && !busy && !error && (
            <p role="status" className="mt-4 text-sm text-ink-2">
              Still couldn&rsquo;t verify your email. Please try again in a minute.
            </p>
          )}

          {error && (
            <div
              role="alert"
              className="mt-4 flex w-full items-start gap-2 rounded-control bg-status-critical/10 p-3 text-left text-sm text-status-critical-fg"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-status-critical" />
              <span>{error}</span>
            </div>
          )}

          <div className="mt-5 w-full">{primary}</div>
          {copy.primary !== 'signout' && (
            <Button type="button" intent="ghost" className="mt-2 w-full" onClick={onSignOut}>
              Sign out
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default NeedsProvisioning;
